import { Upload } from '@aws-sdk/lib-storage';
import { GetObjectCommandInput, PutObjectCommandInput, S3 as AWSS3, ObjectCannedACL, PutObjectAclCommand } from '@aws-sdk/client-s3';
import { createPresignedPost } from '@aws-sdk/s3-presigned-post';
import { Readable } from 'stream';
import { v4 as uuid } from 'uuid';
import { Request } from 'express';

export type NotificationEvent = {
	Records: {
		eventVersion: string;
		eventSource: 'aws:s3';
		awsRegion: string;
		eventTime: string;
		userIdentity: {
			principalId: string;
		};
		requestParameters: {
			sourceIPAddress: string;
		};
		responseElements: {
			'x-amz-request-id': string;
			'x-amz-id-2': string;
		};
		s3: {
			s3SchemaVersion: string;
			configurationId: string;
			bucket: {
				name: string;
				ownerIdentity: {
					principalId: string;
				};
				arn: string;
			};
			object: {
				key: string;
				size: number;
				eTag: string;
				sequencer: string;
			};
		};
	}[];
};

export class S3 {
	private static readonly isDebug = process.env.DEBUG || false;

	private static s3Config = {
		region: process.env.AWS_S3_REGION || process.env.AWS_REGION || '',
		bucketName: process.env.AWS_S3_BUCKET_NAME,
		accessKeyId: process.env.AWS_S3_ACCESS_KEY_ID || process.env.AWS_ACCESS_KEY_ID,
		secretAccessKey: process.env.AWS_S3_SECRET_ACCESS_KEY || process.env.AWS_SECRET_ACCESS_KEY,
		// Role-based runtimes (the import Lambda/Fargate worker) receive TEMPORARY credentials
		// via AWS_ACCESS_KEY_ID/AWS_SECRET_ACCESS_KEY that are only valid WITH the session token.
		// Undefined on Heroku (permanent keys), where passing it through is harmless. Without it,
		// every S3 call from the worker auth-fails (getObjectBytesFromUrl returns null → the
		// import job dies at "request blob fetch failed").
		sessionToken: process.env.AWS_S3_SESSION_TOKEN || process.env.AWS_SESSION_TOKEN
	};

	private static client(): AWSS3 {
		return new AWSS3({
			region: this.s3Config.region,
			credentials: {
				accessKeyId: this.s3Config.accessKeyId,
				secretAccessKey: this.s3Config.secretAccessKey,
				sessionToken: this.s3Config.sessionToken
			}
		});
	}

	public static assetPathForKey(key: string): string {
		return (
			'https://' +
			this.s3Config.bucketName +
			`.s3${this.s3Config.region?.length ? '.' + this.s3Config.region : ''}.amazonaws.com/` +
			key
		);
	}

	public static async createPresignedUpload(
		key: string,
		contentType: string,
		maxBytes: number,
		expiresIn: number = 900,
		tagging?: string
	): Promise<{ url: string; fields: Record<string, string> }> {
		const s3 = this.client();
		const fields: Record<string, string> = {
			acl: ObjectCannedACL.private,
			'Content-Type': contentType
		};
		const conditions: any[] = [
			['eq', '$key', key],
			['content-length-range', 1, maxBytes],
			['eq', '$Content-Type', contentType],
			['eq', '$acl', ObjectCannedACL.private]
		];

		if (tagging) {
			fields['x-amz-tagging'] = tagging;
			conditions.push(['eq', '$x-amz-tagging', tagging]);
		}

		try {
			return await createPresignedPost(s3, {
				Bucket: this.s3Config.bucketName,
				Key: key,
				Fields: fields,
				Conditions: conditions,
				Expires: expiresIn
			});
		} finally {
			s3.destroy();
		}
	}

	public static async setObjectAcl(key: string, acl: ObjectCannedACL): Promise<void> {
		const s3 = this.client();
		try {
			await s3.send(new PutObjectAclCommand({ Bucket: this.s3Config.bucketName, Key: key, ACL: acl }));
		} finally {
			s3.destroy();
		}
	}

	public static async headObject(key: string): Promise<{ exists: boolean; size?: number; contentType?: string }> {
		const s3 = this.client();
		try {
			const head = await s3.headObject({ Bucket: this.s3Config.bucketName, Key: key });
			return {
				exists: true,
				size: typeof head.ContentLength === 'number' ? head.ContentLength : undefined,
				contentType: head.ContentType
			};
		} catch (err) {
			const statusCode = (err as any)?.$metadata?.httpStatusCode;
			const name = (err as any)?.name;
			if (statusCode === 404 || name === 'NotFound' || name === 'NoSuchKey') {
				return { exists: false };
			}
			throw err;
		} finally {
			s3.destroy();
		}
	}

	public static async getObjectRange(key: string, endByte: number): Promise<Buffer> {
		const s3 = this.client();
		try {
			const response = await s3.getObject({
				Bucket: this.s3Config.bucketName,
				Key: key,
				Range: `bytes=0-${endByte}`
			});
			const body = response.Body as { transformToByteArray?: () => Promise<Uint8Array> } | AsyncIterable<Buffer>;
			if (body && typeof (body as { transformToByteArray?: unknown }).transformToByteArray === 'function') {
				return Buffer.from(await (body as { transformToByteArray: () => Promise<Uint8Array> }).transformToByteArray());
			}
			const chunks: Buffer[] = [];
			for await (const chunk of body as AsyncIterable<Buffer>) {
				chunks.push(Buffer.isBuffer(chunk) ? chunk : Buffer.from(chunk));
			}
			return Buffer.concat(chunks);
		} finally {
			s3.destroy();
		}
	}

	public static async deleteObject(key: string): Promise<void> {
		const s3 = this.client();
		try {
			await s3.deleteObject({ Bucket: this.s3Config.bucketName, Key: key });
		} catch (err) {
			if (this.isDebug) {
				console.log(err);
			}
		} finally {
			s3.destroy();
		}
	}

	public static async upload(
		fileBuffer: Buffer,
		fileName: string,
		fileMime: string,
		folderName?: string,
		generateNewName?: boolean,
		// `null` → omit the ACL header entirely (the object is still public via the bucket
		// policy). Needed by the import worker, whose role denies s3:PutObjectAcl so a canned
		// ACL would AccessDenied. Undefined keeps the historical public-read default.
		acl: ObjectCannedACL | null = ObjectCannedACL.public_read,
		tagging?: string
	) {
		const s3 = new AWSS3({
			credentials: {
				accessKeyId: this.s3Config.accessKeyId,
				secretAccessKey: this.s3Config.secretAccessKey,
				sessionToken: this.s3Config.sessionToken
			}
		});

		if (!folderName) {
			folderName = 'uploads/';
		} else if (!folderName.match(/\/$/)) {
			folderName = `${folderName}/`;
		}

		if (generateNewName) {
			fileName = this.generateFileName(fileName);
		}

		console.log('/////////////////S3DEBUG/////////////////');
		console.log('::S3DEBUG:: Bucket Name: ', this.s3Config.bucketName);
		console.log('::S3DEBUG:: Folder Name: ', folderName);
		console.log('::S3DEBUG:: File Name: ', fileName);
		console.log('::S3DEBUG:: ACL: ', acl);
		console.log('::S3DEBUG:: Region: ', this.s3Config.region);
		console.log('/////////////////S3DEBUG/////////////////');

		const file: PutObjectCommandInput = {
			Bucket: this.s3Config.bucketName,
			Key: folderName + fileName,
			Body: fileBuffer,
			ContentType: fileMime
		};

		// Only send the ACL header when an ACL is requested; `null` omits it (see above).
		if (acl) {
			file.ACL = acl;
		}

		if (tagging) {
			file.Tagging = tagging;
		}

		let uploadError: unknown;
		const response = await s3
			.putObject(file)
			.catch(err => {
				uploadError = err;
				if (this.isDebug) {
					console.log(err);
				}
				return false;
			});

		s3.destroy();

		if (response) {
			if (this.isDebug) {
				console.log('File uploaded to S3.');
			}
			return {
				status: 'success',
				message: 'File uploaded to s3.',
				path: 'https://' + this.s3Config.bucketName + `.s3${this.s3Config.region?.length ? '.' + this.s3Config.region : ''}.amazonaws.com/` + folderName + fileName
			};
		} else {
			// Throw a real Error carrying the underlying S3 cause. Previously this threw a bare
			// string, so every caller that logged `err.message` saw `undefined` — which hid an
			// AccessDenied for hours during the .fig-import bring-up.
			const cause = uploadError instanceof Error ? uploadError.message : String(uploadError);
			throw new Error(`Couldn't write file to s3 (${folderName + fileName}): ${cause}`);
		}
	}

	public static async uploadStream(
		fileBuffer: Readable,
		fileName: string,
		fileMime: string,
		folderName?: string,
		generateNewName?: boolean
	) {
		const s3 = new AWSS3({
			credentials: {
				accessKeyId: this.s3Config.accessKeyId,
				secretAccessKey: this.s3Config.secretAccessKey,
				sessionToken: this.s3Config.sessionToken
			}
		});

		if (!folderName) {
			folderName = 'uploads/';
		} else if (!folderName.match(/\/$/)) {
			folderName = `${folderName}/`;
		}

		if (generateNewName) {
			fileName = this.generateFileName(fileName);
		}

		let file: PutObjectCommandInput = {
			Bucket: this.s3Config.bucketName,
			Key: folderName + fileName,
			Body: fileBuffer,
			ContentType: fileMime,
			ACL: ObjectCannedACL.public_read
		};

		let response = await new Upload({
			client: s3,
			params: file
		})
		.done()
		.catch(err => {
			console.log(err);
			return false;
		});

		s3.destroy();

		if (response) {
			console.log('File uploaded to S3.');
			return {
				status: 'succeeded',
				message: 'File uploaded to s3.',
				path: 'https://' + this.s3Config.bucketName + `.s3${this.s3Config.region?.length ? '.' + this.s3Config.region : ''}.amazonaws.com/` + folderName + fileName
			};
		} else {
			throw 'Couldn\'t write file to s3...';
		}
	}

	public static async getFileStream(fileKey: string, req?: Request): Promise<{ stream: Readable; contentType: string }> {
		const params: GetObjectCommandInput = {
			Bucket: this.s3Config.bucketName,
			Key: fileKey
		};

		// In AWS SDK v3, httpOptions are set via the requestHandler. 
		// Use NodeHttpHandler to set timeouts.
		const timeoutInMs = 20 * 60 * 1000;
		const s3 = new AWSS3([{
			requestHandler: {
				connectionTimeout: timeoutInMs,
				socketTimeout: timeoutInMs
			},
			credentials: {
				accessKeyId: this.s3Config.accessKeyId,
				secretAccessKey: this.s3Config.secretAccessKey,
				sessionToken: this.s3Config.sessionToken
			}
		}]);

		const headObjectResponse = await s3
			.headObject(params)
			.catch(err => {
				console.log(err);
				return null;
			});

		s3.destroy();

		if (!headObjectResponse) {
			throw new Error(`Couldn't get file headers.`);
		}

		const contentType = headObjectResponse.ContentType || 'application/octet-stream';

		if (req?.headers?.range) {
			params.Range = req.headers.range;
		}

		const getObjectResponse = await s3.getObject(params);
		const stream = getObjectResponse.Body as Readable;

		return { stream, contentType };
	}

	/**
	 * Build the public object URL for a folder + file using the SAME format
	 * `upload` returns, so callers (e.g. orphan-recovery re-push) can reconstruct
	 * a previously-written object's URL deterministically without storing it.
	 */
	public static objectUrl(folder: string, fileName: string): string {
		const f = folder.endsWith('/') ? folder : `${folder}/`;
		return this.assetPathForKey(f + fileName);
	}

	/**
	 * Return an object's size in bytes via a HEAD (metadata only, no download).
	 * Used by async-import tier routing to size a source file without parsing it.
	 * Returns null when the object is missing or has no ContentLength.
	 */
	public static async getObjectSize(fileKey: string, bucket?: string): Promise<number | null> {
		const s3 = new AWSS3({
			credentials: {
				accessKeyId: this.s3Config.accessKeyId,
				secretAccessKey: this.s3Config.secretAccessKey,
				sessionToken: this.s3Config.sessionToken
			}
		});
		try {
			const head = await s3.headObject({ Bucket: bucket || this.s3Config.bucketName, Key: fileKey });
			return typeof head?.ContentLength === 'number' ? head.ContentLength : null;
		} catch (err) {
			if (this.isDebug) {
				console.log(err);
			}
			return null;
		} finally {
			s3.destroy();
		}
	}

	/**
	 * Fetch an object's full bytes given its virtual-hosted S3 URL (the form
	 * `upload`/`objectUrl` produce). Used by the async-import worker to read a
	 * source file from S3 directly — no DB lookup, no presigned URL. Returns null
	 * on failure.
	 */
	public static async getObjectBytesFromUrl(url: string): Promise<Buffer | null> {
		let parsed: URL;
		try {
			parsed = new URL(url);
		} catch {
			return null;
		}
		const match = parsed.hostname.match(/^([^.]+)\.s3(?:[.-][a-z0-9-]+)?\.amazonaws\.com$/i);
		const bucket = match ? match[1] : this.s3Config.bucketName;
		const key = decodeURIComponent(parsed.pathname.replace(/^\/+/, ''));
		if (!key) {
			return null;
		}
		const s3 = new AWSS3({
			credentials: {
				accessKeyId: this.s3Config.accessKeyId,
				secretAccessKey: this.s3Config.secretAccessKey,
				sessionToken: this.s3Config.sessionToken
			}
		});
		try {
			const res = await s3.getObject({ Bucket: bucket, Key: key });
			const body = res.Body as { transformToByteArray?: () => Promise<Uint8Array> } | AsyncIterable<Buffer>;
			if (body && typeof (body as { transformToByteArray?: unknown }).transformToByteArray === 'function') {
				return Buffer.from(await (body as { transformToByteArray: () => Promise<Uint8Array> }).transformToByteArray());
			}
			const chunks: Buffer[] = [];
			for await (const chunk of body as AsyncIterable<Buffer>) {
				chunks.push(Buffer.isBuffer(chunk) ? chunk : Buffer.from(chunk));
			}
			return Buffer.concat(chunks);
		} catch (err) {
			if (this.isDebug) {
				console.log(err);
			}
			return null;
		} finally {
			s3.destroy();
		}
	}

	public static async remove(objects) {

		let params = {
			Bucket: this.s3Config.bucketName,
			Delete: {
				Objects: objects
			}
		};

		let s3 = new AWSS3({
			credentials: {
				accessKeyId: this.s3Config.accessKeyId,
				secretAccessKey: this.s3Config.secretAccessKey,
				sessionToken: this.s3Config.sessionToken
			}
		});

		let response = await s3
			.deleteObjects(params)
			.catch(err => {
				console.log(err);
				return false;
			});

		s3.destroy();

		if (response) {
			console.log('File(s) removed from S3.');
			return response;
		} else {
			throw 'Error removing file(s)...';
		}
	}

	public static async getContents(folderName: string, recursive: boolean = false, continuationToken?: string, progress: any[] = []) {
		if (!folderName) {
			folderName = 'uploads/';
		}

		let params: any = {
			Bucket: this.s3Config.bucketName,
			Prefix: folderName
		};

		if (continuationToken) {
			params.ContinuationToken = continuationToken;
		}

		let s3 = new AWSS3({
			credentials: {
				accessKeyId: this.s3Config.accessKeyId,
				secretAccessKey: this.s3Config.secretAccessKey,
				sessionToken: this.s3Config.sessionToken
			}
		});

		let response = await s3
			.listObjectsV2(params)
			.catch(err => {
				console.log(err);
				return null;
			});

		s3.destroy();

		if (response) {
			if (recursive) {
				if (!response.Contents) {
					response.Contents = [];
				}
				if (response.NextContinuationToken) {
					return this.getContents(folderName, true, response.NextContinuationToken, [...response.Contents, ...progress]);
				} else {
					response.Contents = [...response.Contents, ...progress];
					return response;
				}
			} else {
				return response;
			}
		} else {
			throw new Error('Error listing bucket contents...');
		}
	}

	public static generateFileName(originalName) {
		let segments = originalName.match(/(.*)\.(.*)$/);

		return uuid() + '.' + segments[2];
	}
}
